<template>
  <div class="audio-tracks" ref="root">
    <div
      class="audio-tracks-scroll"
      ref="scrollContainer"
      :style="`overflow: hidden; white-space: nowrap; height: 100%;`"
    >
      <!-- Left padding = half screen so time 0 starts at center (same as BR) -->
      <div :style="`width: ${halfScreen}px; display: inline-block; height: 100%;`"></div>
      <!-- Timeline at same scale as BR -->
      <div
        class="audio-timeline"
        :style="`width: ${timelineWidth}px; display: inline-block; position: relative; height: 100%;`"
      >
        <!-- One row per character -->
        <div
          v-for="(charSources, character) in groupedSources"
          :key="character"
          class="audio-track-row"
          :style="`height: ${trackHeight}px; position: relative; border-bottom: 1px solid #333;`"
        >
          <!-- Character label -->
          <span class="track-character-label" :style="{ color: charactersColors[character] || '#888' }">
            {{ character }}
          </span>
          <!-- Audio segments -->
          <div
            v-for="source in charSources"
            :key="source.id"
            class="audio-segment"
            :style="{
              position: 'absolute',
              left: (source.start * pxPerSec) + 'px',
              width: ((source.end - source.start) * pxPerSec) + 'px',
              height: '100%',
              backgroundColor: hexToRgba(charactersColors[character] || '#6449D1', 0.6),
              borderRadius: '3px',
              border: '1px solid ' + (charactersColors[character] || '#6449D1'),
            }"
            :title="`${source.character} — ${source.start.toFixed(1)}s → ${source.end.toFixed(1)}s`"
          >
            <div class="segment-label">{{ source.character }}</div>
          </div>
        </div>
      </div>
      <!-- Right padding -->
      <div :style="`width: ${halfScreen}px; display: inline-block; height: 100%;`"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AudioTracks",

  props: {
    sources: { type: Array, default: () => [] },
    charactersColors: { type: Object, default: () => ({}) },
    video: { default: null },
    minPxPerSec: { type: Number, default: 25 },
    videoDuration: { type: Number, default: 0 },
    offsetDiv: { default: null },
  },

  data() {
    return {
      trackHeight: 36,
      halfScreen: 0,
      _scrollRAF: null,
    };
  },

  computed: {
    pxPerSec() {
      return this.minPxPerSec;
    },
    timelineWidth() {
      return this.videoDuration * this.pxPerSec;
    },
    groupedSources() {
      const groups = {};
      for (const s of this.sources) {
        if (!groups[s.character]) groups[s.character] = [];
        groups[s.character].push(s);
      }
      return groups;
    },
  },

  mounted() {
    this.halfScreen = Math.round(window.innerWidth / 2);
    this.startScrollSync();
    window.addEventListener("resize", this.onResize);
  },

  beforeDestroy() {
    if (this._scrollRAF) cancelAnimationFrame(this._scrollRAF);
    window.removeEventListener("resize", this.onResize);
  },

  methods: {
    onResize() {
      this.halfScreen = Math.round(window.innerWidth / 2);
    },

    startScrollSync() {
      const sync = () => {
        const od = this.offsetDiv;
        const sc = this.$refs.scrollContainer;
        if (od && sc) {
          sc.scrollLeft = od.scrollLeft;
        }
        this._scrollRAF = requestAnimationFrame(sync);
      };
      this._scrollRAF = requestAnimationFrame(sync);
    },

    hexToRgba(hex, alpha) {
      if (!hex || !hex.startsWith("#")) return `rgba(100, 73, 209, ${alpha})`;
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
  },
};
</script>

<style>
.audio-tracks {
  width: 100%;
  background: #1a1a1a;
  border-top: 2px solid #e74970;
}

.audio-tracks-scroll {
  overflow: hidden;
}

.audio-track-row {
  background: #111;
}

.track-character-label {
  position: sticky;
  left: 8px;
  font-size: 10px;
  font-weight: bold;
  z-index: 10;
  pointer-events: none;
  opacity: 0.7;
  line-height: 36px;
}

.audio-segment {
  overflow: hidden;
  cursor: pointer;
  transition: opacity 0.15s;
}

.audio-segment:hover {
  opacity: 0.8;
}

.segment-label {
  font-size: 9px;
  color: rgba(255,255,255,0.7);
  padding: 2px 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
